// Who is signed in and what they may see — provided by the auth gate from GET /api/v1/auth/me.
// Parts read it with useAuth() / usePerms(); outside the gate (sign-in screen, isolated tests) it is empty (everything off).
import { createContext, useContext, useMemo, type ReactNode } from 'react';
import type { AuthMe } from '../api/types';
import { useHasT, useLang, useT } from '../i18n';
import { can as canPerm, scopeOf as scopeOfPerm, type EffectivePerms, type ScopeValue } from './permissions';
import type { Scope } from '@aiax-hub/contracts';
import { DisplayTimezoneContext } from '../lib/display-timezone';
import { DisplayPreferencesContext } from '../lib/display-preferences';
import { TIME_ZONE } from '../lib/time';

export interface AuthValue {
  me: AuthMe | null;
  perms: EffectivePerms;
}
const EMPTY: AuthValue = { me: null, perms: {} };
const AuthContext = createContext<AuthValue>(EMPTY);

export function AuthProvider({ me, children }: { me: AuthMe | null; children: ReactNode }) {
  const value = useMemo<AuthValue>(() => ({ me, perms: (me?.permissions ?? {}) as EffectivePerms }), [me]);
  return (
    <AuthContext.Provider value={value}>
      <DisplayTimezoneContext.Provider value={me?.prefs.timezone ?? me?.org.timezone ?? TIME_ZONE}>
        <DisplayPreferencesContext.Provider value={me?.prefs ?? { numberUnit: 'compact', wrapLongText: true }}>
          {children}
        </DisplayPreferencesContext.Provider>
      </DisplayTimezoneContext.Provider>
    </AuthContext.Provider>
  );
}

export const useAuth = (): AuthValue => useContext(AuthContext);
export const usePerms = (): EffectivePerms => useContext(AuthContext).perms;

/** Permission helpers bound to the signed-in person: can('people.view', 'team'), scopeOf('logs.list'). */
export function useCan() {
  const perms = usePerms();
  return useMemo(
    () => ({
      can: (key: string, need?: Scope) => canPerm(perms, key, need),
      scopeOf: (key: string): ScopeValue => scopeOfPerm(perms, key),
    }),
    [perms],
  );
}

/**
 * The badge role of the signed-in person (`primaryRole`, plan API-02) and its name: system roles are named by the web
 * (`role.<id>`), custom roles carry their own names. Display only — permissions never come from it.
 */
export function useMyRole(): { id: string; name: string } {
  const { me } = useAuth();
  const t = useT();
  const hasT = useHasT();
  const lang = useLang();
  if (!me) return { id: '', name: '' };
  const id = me.primaryRole;
  const held = me.roles.find((r) => r.roleId === id);
  const name = held?.kind === 'custom' && held.name ? held.name[lang] || held.name.ko : hasT('role.' + id) ? t('role.' + id) : id;
  return { id, name };
}
